import type { UserManager } from 'oidc-client-ts';
import { ApiClient, type ApiClientOptions } from '../api/client';
import type { AppConfig } from '../api/types';
import { CALLBACK_PATH, createUserManager, type ReturnState } from './oidc';

export interface BootResult {
  client: ApiClient;
  config: AppConfig;
  userManager?: UserManager;
}

/**
 * Loads `GET /config` and, in `jwt` mode, completes or starts the sign-in.
 * Returns null while the browser is being redirected to the identity provider.
 */
export async function bootstrap(options: ApiClientOptions): Promise<BootResult | null> {
  const anonymous = new ApiClient(options);
  const config = await anonymous.getConfig();
  if (config.auth.mode !== 'jwt') return { client: anonymous, config };

  const userManager = createUserManager(config.auth);
  if (window.location.pathname === CALLBACK_PATH) {
    const user = await userManager.signinRedirectCallback();
    const returnTo = (user.state as ReturnState | undefined)?.returnTo ?? '/';
    window.history.replaceState(null, '', returnTo);
  }

  let user = await userManager.getUser();
  if (user?.expired) {
    try {
      user = await userManager.signinSilent();
    } catch {
      user = null;
    }
  }
  if (!user) {
    const returnTo = window.location.pathname + window.location.search;
    await userManager.signinRedirect({ state: { returnTo } satisfies ReturnState });
    return null;
  }

  const client = new ApiClient({
    ...options,
    getAccessToken: async () => (await userManager.getUser())?.access_token ?? null,
  });
  return { client, config, userManager };
}
